/*
 * Copyright The OpenTelemetry Authors
 * SPDX-License-Identifier: Apache-2.0
 */

import { Attributes } from '@opentelemetry/api';
import {
  ATTR_HTTP_ROUTE,
  ATTR_HTTP_REQUEST_METHOD,
} from '@opentelemetry/semantic-conventions';
import type * as Hapi from '@hapi/hapi';
import {
  HapiLayerType,
  HapiLifecycleMethodNames,
  HapiPluginObject,
  PatchableExtMethod,
  ServerExtDirectInput,
} from './internal-types';
import { AttributeNames } from './enums/AttributeNames';

export function getPluginName<T>(plugin: Hapi.Plugin<T>): string {
  if ((plugin as Hapi.PluginNameVersion).name) {
    return (plugin as Hapi.PluginNameVersion).name;
  } else {
    return (plugin as Hapi.PluginPackage).pkg.name;
  }
}

export const isLifecycleExtType = (
  variableToCheck: unknown
): variableToCheck is Hapi.ServerRequestExtType => {
  return (
    typeof variableToCheck === 'string' &&
    HapiLifecycleMethodNames.has(variableToCheck)
  );
};

export const isLifecycleExtEventObj = (
  variableToCheck: unknown
): variableToCheck is Hapi.ServerExtEventsRequestObject => {
  const event = (variableToCheck as Hapi.ServerExtEventsRequestObject)?.type;
  return event !== undefined && isLifecycleExtType(event);
};

export const isDirectExtInput = (
  variableToCheck: unknown
): variableToCheck is ServerExtDirectInput => {
  return (
    Array.isArray(variableToCheck) &&
    variableToCheck.length <= 3 &&
    isLifecycleExtType(variableToCheck[0]) &&
    typeof variableToCheck[1] === 'function'
  );
};

export const isPatchableExtMethod = (
  variableToCheck: PatchableExtMethod | PatchableExtMethod[]
): variableToCheck is PatchableExtMethod => {
  return !Array.isArray(variableToCheck);
};

export const getRouteMetadata = (
  route: Hapi.ServerRoute,
  pluginName?: string
): {
  attributes: Attributes;
  name: string;
} => {
  const attributes: Attributes = {
    [ATTR_HTTP_ROUTE]: route.path,
  };
  // Note: This currently does *not* normalize the method name to uppercase
  // and conditionally include `http.request.method.original` as described
  // at https://opentelemetry.io/docs/specs/semconv/http/http-spans/
  // These attributes are for a *hapi* span, and not the parent HTTP span,
  // so the HTTP span guidance doesn't strictly apply.
  attributes[ATTR_HTTP_REQUEST_METHOD] = route.method;

  let name;
  if (pluginName) {
    attributes[AttributeNames.HAPI_TYPE] = HapiLayerType.PLUGIN;
    attributes[AttributeNames.PLUGIN_NAME] = pluginName;
    name = `${pluginName}: route - ${route.path}`;
  } else {
    attributes[AttributeNames.HAPI_TYPE] = HapiLayerType.ROUTER;
    name = `route - ${route.path}`;
  }

  return { attributes, name };
};

export const getExtMetadata = (
  extPoint: Hapi.ServerRequestExtType,
  pluginName?: string,
  methodName?: string
): {
  attributes: Attributes;
  name: string;
} => {
  let baseName = `ext - ${extPoint}`;
  if (methodName && methodName !== 'method') {
    // method is the default name for the extension in the ServerExtEventsObject format.
    baseName = `ext - ${extPoint} - ${methodName}`;
  }
  if (pluginName) {
    return {
      attributes: {
        [AttributeNames.EXT_TYPE]: extPoint,
        [AttributeNames.HAPI_TYPE]: HapiLayerType.EXT,
        [AttributeNames.PLUGIN_NAME]: pluginName,
      },
      name: `${pluginName}: ${baseName}`,
    };
  }
  return {
    attributes: {
      [AttributeNames.EXT_TYPE]: extPoint,
      [AttributeNames.HAPI_TYPE]: HapiLayerType.EXT,
    },
    name: baseName,
  };
};

export const getPluginFromInput = <T>(
  pluginObj: HapiPluginObject<T>
): Hapi.Plugin<T, void> => {
  if ('plugin' in pluginObj) {
    if ('plugin' in pluginObj.plugin) {
      return pluginObj.plugin.plugin;
    }
    return pluginObj.plugin;
  }
  return pluginObj;
};
